import { CommonDefaultFilterKeys } from '@ardoq/api-types';
import type { Formatting, FormattingGroup } from './types';
import {
  CUSTOM_FIELDS_FORMATTING_GROUP_LABEL,
  DEFAULT_FORMATTING_GROUP_LABEL,
} from '../../consts';
import { type I18nLocale, t } from '@ardoq/i18n';

type GetFilterGroupsArgs = {
  i18nLocale: I18nLocale;
  filterGroups: FormattingGroup[];
  typeNames: string[] | null;
  invalidFormattingRuleFilter?: Formatting;
  isNewConditionalFormatting: boolean;
};

export const getFilterGroups = ({
  i18nLocale,
  filterGroups: originalFilterGroups,
  typeNames,
  invalidFormattingRuleFilter,
  isNewConditionalFormatting,
}: GetFilterGroupsArgs) => {
  const filterGroups = structuredClone(originalFilterGroups);

  // TYPE field only works when exactly one component/reference type is selected
  if (isNewConditionalFormatting && (!typeNames || typeNames.length !== 1)) {
    const typeFilter = filterGroups
      .find(filters => filters.label === DEFAULT_FORMATTING_GROUP_LABEL)
      ?.filters.find(({ id }) => id === CommonDefaultFilterKeys.TYPE);
    if (typeFilter) {
      typeFilter.isDisabled = true;
      typeFilter.popoverContent =
        typeNames && typeNames.length > 1
          ? t(
              i18nLocale,
              'perspectives:filterGroups.typeRequiresSingleTypePopover',
              "This can't be applied to multiple types. Select a single type first."
            )
          : t(
              i18nLocale,
              'perspectives:filterGroups.typeRequiresSpecificTypePopover',
              "This can't be applied to all types. Select a specific type first."
            );
    }
  }

  if (!invalidFormattingRuleFilter) {
    return filterGroups;
  }

  if (invalidFormattingRuleFilter.id === 'type') {
    const defaultFilters = filterGroups.find(
      filters => filters.label === DEFAULT_FORMATTING_GROUP_LABEL
    )?.filters;

    if (!defaultFilters) {
      return filterGroups;
    }

    const indexOfTypeFilter = defaultFilters.findIndex(
      filter => filter.id === 'type'
    );

    if (indexOfTypeFilter === -1) {
      return filterGroups;
    }

    defaultFilters[indexOfTypeFilter] = invalidFormattingRuleFilter;

    return filterGroups;
  }

  const customFilters = filterGroups.find(
    filters => filters.label === CUSTOM_FIELDS_FORMATTING_GROUP_LABEL
  )?.filters;

  if (!customFilters) {
    return [
      ...filterGroups,
      {
        label: CUSTOM_FIELDS_FORMATTING_GROUP_LABEL,
        filters: [invalidFormattingRuleFilter],
      },
    ];
  }

  customFilters.push(invalidFormattingRuleFilter);

  return filterGroups;
};
